// Game logic for frontend validation - mirrors backend logic
class GameLogic {
  constructor() {
    this.pieceTypes = {
      KING: 'king',
      KNIGHT: 'knight', 
      PAWN: 'pawn',
      ADVISOR: 'advisor',
      CANNON: 'cannon',
      CAMEL: 'camel',
      DONKEY: 'donkey',
      RAM: 'ram',
      SPY: 'spy'
    };
    this.spyFreezeMap = new Map(); // Track which kings are frozen by spies: {spyPos: {row, col}, kingFrozenAt: {row, col}}
    
    // VALIDATION MODE - Now using server-side validation only
    // Client does minimal validation, server handles all piece-specific rules
    this.bypassValidation = true;           // CLIENT BYPASS: Let server handle validation
    this.enableBasicValidation = true;      // Board bounds, same square, own piece capture
    this.enableCheckValidation = false;     // Check/checkmate validation (disabled)
    this.enableSpyValidation = false;       // Spy freeze validation (disabled)
    
    // Selective piece validation - enable pieces one at a time
    this.enablePieceValidation = {
      PAWN: true,      // Enable pawn validation
      KING: true,      // Enable king validation
      KNIGHT: true,    // Enable knight validation
      ADVISOR: true,   // Enable advisor validation
      CANNON: false,   // Disable cannon validation
      CAMEL: true,     // Enable camel validation
      DONKEY: {
        enabled: true,    // Overall donkey validation - ENABLED
        A: true,          // Piece A: Vertical movement (odd/even row pattern) - ENABLED
        B: true,          // Piece B: Horizontal movement (odd/even col pattern) - ENABLED
        C: true           // Piece C: King-like movement (1 square any direction) - ENABLED
      },
      RAM: true,       // Enable ram validation
      SPY: true        // Enable spy validation
    };
  }

  isValidMove(board, fromRow, fromCol, toRow, toCol, currentPlayer) {
    const piece = board[fromRow][fromCol];
    
    if (!piece || piece.color !== currentPlayer) {
      return false;
    }

    // BYPASS MODE - Allow all moves if bypassing validation
    if (this.bypassValidation) {
      console.log('🔓 CLIENT BYPASS MODE: Basic validation only - server handles piece rules');
      
      // Only do basic validation if enabled
      if (this.enableBasicValidation) {
        // Basic validation
        if (toRow < 0 || toRow >= 9 || toCol < 0 || toCol >= 9) {
          console.log('❌ Basic validation failed: out of bounds');
          return false;
        }

        if (fromRow === toRow && fromCol === toCol) {
          console.log('❌ Basic validation failed: same square');
          return false;
        }

        const targetPiece = board[toRow][toCol];

        if (targetPiece && targetPiece.color === piece.color) {
          // Ram may bump own pieces (server enforces the actual bump rules —
          // empty square behind, not off-board, not into own king, not into
          // self-check). Let the request through so the server can validate.
          const isRamFriendlyBumpCandidate =
            piece.type === 'ram' && targetPiece.type !== 'king';
          if (!isRamFriendlyBumpCandidate) {
            console.log('❌ Basic validation failed: capturing own piece');
            return false;
          }
        }
      }
      
      console.log('✅ CLIENT BYPASS: Basic checks passed - server will validate piece rules');
      return true;
    }

    // NORMAL MODE - Full validation
    console.log('🔒 NORMAL MODE: Full validation');

    // Basic validation
    if (toRow < 0 || toRow >= 9 || toCol < 0 || toCol >= 9) {
      return false;
    }

    if (fromRow === toRow && fromCol === toCol) {
      return false;
    }

    const targetPiece = board[toRow][toCol];
    
    if (targetPiece && targetPiece.color === piece.color) {
      return false;
    }

    // Check if king is frozen by spy (only if spy validation is enabled)
    if (this.enableSpyValidation && piece.type === this.pieceTypes.KING && this.spyFreezeMap.has(piece.color)) {
      const freezeInfo = this.spyFreezeMap.get(piece.color);
      const spyStillAlive = board[freezeInfo.spyPos.row][freezeInfo.spyPos.col];
      
      if (spyStillAlive && spyStillAlive.type === this.pieceTypes.SPY) {
        // King is frozen - can only move within 3x3 area around frozen position
        const frozenRow = freezeInfo.kingFrozenAt.row;
        const frozenCol = freezeInfo.kingFrozenAt.col;
        
        const deltaRow = Math.abs(toRow - frozenRow);
        const deltaCol = Math.abs(toCol - frozenCol);
        
        if (deltaRow > 1 || deltaCol > 1) {
          return false; // Outside the 3x3 freeze zone
        }
      } else {
        this.spyFreezeMap.delete(piece.color); // Spy was captured, unfreeze king
      }
    }

    // Piece-specific validation (only if enabled for that piece type)
    let result = true; // Default to true when piece validation is disabled
    switch (piece.type) {
      case this.pieceTypes.KING:
        result = this.enablePieceValidation.KING ? 
          this.validateKingMove(fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.KNIGHT:
        result = this.enablePieceValidation.KNIGHT ? 
          this.validateKnightMove(fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.PAWN:
        result = this.enablePieceValidation.PAWN ? 
          this.validatePawnMove(board, fromRow, fromCol, toRow, toCol, piece) : true;
        break;
      case this.pieceTypes.ADVISOR:
        result = this.enablePieceValidation.ADVISOR ? 
          this.validateAdvisorMove(board, fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.CANNON:
        result = this.enablePieceValidation.CANNON ? 
          this.validateCannonMove(board, fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.CAMEL:
        result = this.enablePieceValidation.CAMEL ? 
          this.validateCamelMove(fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.DONKEY:
        result = this.enablePieceValidation.DONKEY.enabled ? 
          this.validateDonkeyMove(board, fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.RAM:
        result = this.enablePieceValidation.RAM ? 
          this.validateRamMove(board, fromRow, fromCol, toRow, toCol) : true;
        break;
      case this.pieceTypes.SPY:
        result = this.enablePieceValidation.SPY ? 
          this.validateSpyMove(board, fromRow, fromCol, toRow, toCol, piece) : true;
        break;
      default:
        result = false;
    }
    
    return result;
  }

  validateKingMove(fromRow, fromCol, toRow, toCol) {
    const deltaRow = Math.abs(toRow - fromRow);
    const deltaCol = Math.abs(toCol - fromCol);
    return deltaRow <= 1 && deltaCol <= 1;
  }

  validateKnightMove(fromRow, fromCol, toRow, toCol) {
    const deltaRow = Math.abs(toRow - fromRow);
    const deltaCol = Math.abs(toCol - fromCol);
    return (deltaRow === 2 && deltaCol === 1) || (deltaRow === 1 && deltaCol === 2);
  }

  validatePawnMove(board, fromRow, fromCol, toRow, toCol, piece) {
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;
    const direction = piece.color === 'white' ? 1 : -1;

    // Forward move - only one square forward
    if (deltaCol === 0) {
      // One square forward
      if (deltaRow === direction) {
        return !board[toRow][toCol]; // Square must be empty
      }
    }

    // Diagonal capture
    if (Math.abs(deltaCol) === 1 && deltaRow === direction) {
      const targetPiece = board[toRow][toCol];
      return targetPiece && targetPiece.color !== piece.color;
    }

    return false;
  }

  validateAdvisorMove(board, fromRow, fromCol, toRow, toCol) {
    const deltaRow = Math.abs(toRow - fromRow);
    const deltaCol = Math.abs(toCol - fromCol);
    const maxDistance = Math.max(deltaRow, deltaCol);
    
    if (maxDistance < 1 || maxDistance > 2) {
      return false;
    }

    // Check if path is clear for 2-square moves
    if (maxDistance === 2) {
      return this.isPathClear(board, fromRow, fromCol, toRow, toCol);
    }

    return true;
  }

  validateCannonMove(board, fromRow, fromCol, toRow, toCol) {
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;

    // Cannon moves like a rook (horizontal or vertical)
    if (deltaRow !== 0 && deltaCol !== 0) {
      return false;
    }

    const targetPiece = board[toRow][toCol];

    // If target square is empty, move normally (like rook)
    if (!targetPiece) {
      return this.isPathClear(board, fromRow, fromCol, toRow, toCol);
    }

    // If target square has a piece, must jump over exactly one piece to capture
    return this.canCannonJumpAndCapture(board, fromRow, fromCol, toRow, toCol);
  }

  validateCamelMove(fromRow, fromCol, toRow, toCol) {
    const deltaRow = Math.abs(toRow - fromRow);
    const deltaCol = Math.abs(toCol - fromCol);
    return (deltaRow === 3 && deltaCol === 1) || (deltaRow === 1 && deltaCol === 3);
  }

  validateDonkeyMove(board, fromRow, fromCol, toRow, toCol) {
    // Donkey movement is the combination of three pieces: A, B, and C
    // The donkey can move like A OR B OR C
    // Check individual flags to enable each type of movement separately
    
    // Piece C: King-like movement (1 square in any direction)
    if (this.enablePieceValidation.DONKEY.C && this.isKingLikeMove(fromRow, fromCol, toRow, toCol)) {
      return true; // King moves don't need path checking
    }

    // Piece A: Vertical movement with alternating pattern
    if (this.enablePieceValidation.DONKEY.A && this.isPieceAMove(fromRow, fromCol, toRow, toCol)) {
      const isValid = this.isPieceAPathClear(board, fromRow, fromCol, toRow, toCol);
      if (isValid) return true;
    }

    // Piece B: Horizontal movement with alternating pattern  
    if (this.enablePieceValidation.DONKEY.B && this.isPieceBMove(fromRow, fromCol, toRow, toCol)) {
      const isValid = this.isPieceBPathClear(board, fromRow, fromCol, toRow, toCol);
      if (isValid) return true;
    }
    return false;
  }

  isPieceAPathClear(board, fromRow, fromCol, toRow, toCol) {
    // Piece A: Vertical movement with alternating column pattern
    // Check if path is clear, but only for squares the donkey can actually reach
    const path = this.generatePieceAPath(fromRow, fromCol, toRow, toCol);
    
    // Check each square in the path (except start and end)
    for (let i = 1; i < path.length - 1; i++) {
      const square = path[i];
      const piece = board[square.row][square.col];
      
      // If there's a piece on a square the donkey must pass through, it's blocked
      if (piece) {
        return false;
      }
    }
    
    return true;
  }

  generatePieceAPath(fromRow, fromCol, toRow, toCol) {
    // Generate the actual path for Piece A movement (vertical within 3 adjacent files)
    const path = [{ row: fromRow, col: fromCol }];
    
    // For Piece A, the movement pattern depends on column relationship
    const deltaCol = Math.abs(toCol - fromCol);
    
    if (deltaCol === 0) {
      // Same column: move through odd row indices only (1,3,5,7)
      const rowDirection = toRow > fromRow ? 2 : -2;
      let currentRow = fromRow;
      
      while (currentRow !== toRow) {
        currentRow += rowDirection;
        if (currentRow >= 0 && currentRow <= 8) {
          path.push({ row: currentRow, col: fromCol });
        }
      }
    } else if (deltaCol === 1) {
      // Adjacent column: move through even row indices only (0,2,4,6,8)
      const rowDirection = toRow > fromRow ? 1 : -1;
      let currentRow = fromRow;
      let currentCol = fromCol;
      
      while (currentRow !== toRow) {
        currentRow += rowDirection;
        // For adjacent columns, we can move to any even row index
        if (currentRow >= 0 && currentRow <= 8 && currentRow % 2 === 0) {
          path.push({ row: currentRow, col: currentCol });
        }
      }
      
      // Final move to the target column
      if (currentCol !== toCol) {
        currentCol = toCol;
        path.push({ row: currentRow, col: currentCol });
      }
    }
    
    return path;
  }

  isPieceBPathClear(board, fromRow, fromCol, toRow, toCol) {
    // Piece B: Horizontal movement with alternating row pattern
    // CRITICAL FIX: Donkey can choose up OR down path, only blocked if BOTH are blocked
    const deltaRow = Math.abs(toRow - fromRow);
    
    if (deltaRow === 0) {
      // Same row: check zig-zag pattern (matches C++ logic)
      const colDirection = toCol > fromCol ? 1 : -1;
      let even = true;
      let blockedUp = false;
      let blockedDown = false;
      
      for (let col = fromCol + colDirection; col !== toCol; col += colDirection) {
        if (col < 0 || col > 8) break;
        
        if (even) {
          // Check squares above and below
          // Only set blocked if IN BOUNDS and has a piece
          if (fromRow + 1 <= 8 && board[fromRow + 1][col]) {
            blockedUp = true;
          }
          
          if (fromRow - 1 >= 0 && board[fromRow - 1][col]) {
            blockedDown = true;
          }
          
          // If out of bounds in a direction, that direction is "unavailable" but not "blocked"
          // We need both UP and DOWN to be either blocked OR unavailable
          if (fromRow + 1 > 8) blockedUp = true; // Edge of board counts as blocked
          if (fromRow - 1 < 0) blockedDown = true; // Edge of board counts as blocked
        } else {
          // Check square on same row
          if (board[fromRow][col]) {
            blockedUp = true;
            blockedDown = true;
          }
        }
        even = !even;
      }
      
      // Path is blocked only if BOTH up and down are blocked
      return !(blockedUp && blockedDown);
    } else if (deltaRow === 1) {
      // Adjacent row: check alternating pattern
      const colDirection = toCol > fromCol ? 1 : -1;
      let even = true;
      let blockedUp = false;
      let blockedDown = false;
      
      for (let col = fromCol + colDirection; col !== toCol; col += colDirection) {
        if (col < 0 || col > 8) break;
        
        if (even) {
          if (fromRow + 1 <= 8 && board[fromRow + 1][col]) {
            blockedUp = true;
          }
          if (fromRow + 1 > 8) blockedUp = true;
          
          if (fromRow - 1 >= 0 && board[fromRow - 1][col]) {
            blockedDown = true;
          }
          if (fromRow - 1 < 0) blockedDown = true;
        } else {
          if (board[fromRow][col]) {
            blockedUp = true;
            blockedDown = true;
          }
        }
        even = !even;
      }
      
      // Check which direction we're going and if that path is blocked
      if (toRow > fromRow && blockedUp) return false;
      if (toRow < fromRow && blockedDown) return false;
      if (blockedUp && blockedDown) return false;
      
      return true;
    }
    
    return false;
  }

  generatePieceBPath(fromRow, fromCol, toRow, toCol) {
    // Generate the actual path for Piece B movement (horizontal within 3 adjacent ranks)
    // CRITICAL FIX: Include zig-zag pattern checking for blocking
    const path = [{ row: fromRow, col: fromCol }];
    
    // For Piece B, the movement pattern depends on row relationship
    const deltaRow = Math.abs(toRow - fromRow);
    
    if (deltaRow === 0) {
      // Same row: donkey moves with alternating up/down pattern
      // CRITICAL: Must check positions ABOVE and BELOW intermediate columns
      const colDirection = toCol > fromCol ? 1 : -1;
      let even = true; // Track alternating pattern
      
      for (let col = fromCol + colDirection; col !== toCol + colDirection; col += colDirection) {
        if (col < 0 || col > 8) break;
        
        if (col === toCol) {
          // Final destination
          path.push({ row: fromRow, col: toCol });
        } else {
          // Intermediate columns - check both up and down positions
          if (even) {
            // On even steps, donkey can go through squares above or below
            if (fromRow + 1 <= 8) {
              path.push({ row: fromRow + 1, col: col });
            }
            if (fromRow - 1 >= 0) {
              path.push({ row: fromRow - 1, col: col });
            }
          } else {
            // On odd steps, donkey goes through the same row
            path.push({ row: fromRow, col: col });
          }
          even = !even;
        }
      }
    } else if (deltaRow === 1) {
      // Adjacent row: requires opposite column parity
      const colDirection = toCol > fromCol ? 1 : -1;
      let currentRow = fromRow;
      let currentCol = fromCol;
      
      while (currentCol !== toCol) {
        currentCol += colDirection;
        if (currentCol >= 0 && currentCol <= 8 && currentCol !== toCol) {
          path.push({ row: currentRow, col: currentCol });
        }
      }
      
      // Final move to the target row
      if (currentRow !== toRow) {
        currentRow = toRow;
        path.push({ row: currentRow, col: currentCol });
      }
    }
    
    return path;
  }

  isKingLikeMove(fromRow, fromCol, toRow, toCol) {
    // Piece C: Can move exactly 1 square in any direction
    const deltaRow = Math.abs(toRow - fromRow);
    const deltaCol = Math.abs(toCol - fromCol);
    
    return deltaRow <= 1 && deltaCol <= 1;
  }

  isPieceAMove(fromRow, fromCol, toRow, toCol) {
    // Piece A: Vertical movement within 3 adjacent files
    // Current column ± 1 only
    
    const deltaCol = Math.abs(toCol - fromCol);
    
    // Must be within 3 adjacent columns (current column ± 1)
    if (deltaCol > 1) {
      return false;
    }

    // Same column: same parity (both odd or both even row indices)
    if (toCol === fromCol) {
      return toRow % 2 === fromRow % 2;
    }

    // Adjacent columns: different parity (if source is even, target must be odd, and vice versa)
    if (deltaCol === 1) {
      return toRow % 2 !== fromRow % 2;
    }

    return false;
  }

  isPieceBMove(fromRow, fromCol, toRow, toCol) {
    // Piece B: Horizontal movement within 3 adjacent ranks
    // Current row ± 1 only
    
    const deltaRow = Math.abs(toRow - fromRow);
    
    // Must be within 3 adjacent rows (current row ± 1)
    if (deltaRow > 1) {
      return false;
    }

    // Same row: same parity (both odd or both even column indices)
    if (toRow === fromRow) {
      return toCol % 2 === fromCol % 2;
    }

    // Adjacent rows: different parity (if source is even, target must be odd, and vice versa)
    if (deltaRow === 1) {
      return toCol % 2 !== fromCol % 2;
    }

    return false;
  }



  validateRamMove(board, fromRow, fromCol, toRow, toCol) {
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;

    // Ram moves like a rook (horizontal or vertical)
    if (deltaRow !== 0 && deltaCol !== 0) {
      return false;
    }

    const targetPiece = board[toRow][toCol];

    // If target square is empty, move normally (like rook)
    if (!targetPiece) {
      return this.isPathClear(board, fromRow, fromCol, toRow, toCol);
    }

    // If target square has a piece, check if we can bump it
    return this.canRamBumpPiece(board, fromRow, fromCol, toRow, toCol);
  }

  validateSpyMove(board, fromRow, fromCol, toRow, toCol, piece) {
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;

    // Move 1 space in any direction
    if (Math.abs(deltaRow) <= 1 && Math.abs(deltaCol) <= 1) {
      return true;
    }

    // Move any number of spaces backwards (including diagonally)
    const backwardDirection = piece.color === 'white' ? -1 : 1;
    
    if (deltaRow * backwardDirection > 0) {
      // Moving backwards (toward home row) - can be straight back or diagonal
      return this.isPathClear(board, fromRow, fromCol, toRow, toCol);
    }

    return false;
  }

  canRamBumpPiece(board, fromRow, fromCol, toRow, toCol) {
    // Path must be clear up to the target
    if (!this.isPathClear(board, fromRow, fromCol, toRow, toCol)) {
      return false;
    }

    // Calculate where the bumped piece would go
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;
    
    const stepRow = deltaRow === 0 ? 0 : deltaRow / Math.abs(deltaRow);
    const stepCol = deltaCol === 0 ? 0 : deltaCol / Math.abs(deltaCol);
    
    const bumpRow = toRow + stepRow;
    const bumpCol = toCol + stepCol;

    // If bump target is off the board, that's a capture (valid)
    if (bumpRow < 0 || bumpRow >= 9 || bumpCol < 0 || bumpCol >= 9) {
      return true;
    }

    // If bump target square is occupied, can't bump
    if (board[bumpRow][bumpCol]) {
      return false;
    }

    return true;
  }

  // Check if ram can push king off the board (for check detection)
  canRamPushKingOffBoard(board, ramRow, ramCol, kingRow, kingCol) {
    // Calculate the push direction (same as ram's movement direction)
    const deltaRow = kingRow - ramRow;
    const deltaCol = kingCol - ramCol;
    
    const stepRow = deltaRow === 0 ? 0 : deltaRow / Math.abs(deltaRow);
    const stepCol = deltaCol === 0 ? 0 : deltaCol / Math.abs(deltaCol);
    
    // Check if there's a clear path from the king's position to the edge of the board
    let checkRow = kingRow + stepRow;
    let checkCol = kingCol + stepCol;
    
    // Keep moving in the push direction until we hit the edge or an obstacle
    while (checkRow >= 0 && checkRow < 9 && checkCol >= 0 && checkCol < 9) {
      // If there's a piece blocking the path, king can't be pushed off
      if (board[checkRow][checkCol]) {
        return false;
      }
      
      // Move to next square in push direction
      checkRow += stepRow;
      checkCol += stepCol;
    }
    
    // If we reached here, there's a clear path to the edge - king can be pushed off
    return true;
  }

  canCannonJumpAndCapture(board, fromRow, fromCol, toRow, toCol) {
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;
    
    const stepRow = deltaRow === 0 ? 0 : deltaRow / Math.abs(deltaRow);
    const stepCol = deltaCol === 0 ? 0 : deltaCol / Math.abs(deltaCol);
    
    let currentRow = fromRow + stepRow;
    let currentCol = fromCol + stepCol;
    let jumpPieceFound = false;
    
    // Look for exactly one piece to jump over
    while (currentRow !== toRow || currentCol !== toCol) {
      // Check bounds before accessing board
      if (currentRow < 0 || currentRow >= 9 || currentCol < 0 || currentCol >= 9) {
        return false; // Path goes out of bounds
      }
      
      if (board[currentRow][currentCol]) {
        if (jumpPieceFound) {
          return false; // More than one piece in the way
        }
        jumpPieceFound = true;
      }
      currentRow += stepRow;
      currentCol += stepCol;
    }
    
    // Must have exactly one piece to jump over
    return jumpPieceFound;
  }

  isPathClear(board, fromRow, fromCol, toRow, toCol) {
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;
    
    const stepRow = deltaRow === 0 ? 0 : deltaRow / Math.abs(deltaRow);
    const stepCol = deltaCol === 0 ? 0 : deltaCol / Math.abs(deltaCol);
    
    let currentRow = fromRow + stepRow;
    let currentCol = fromCol + stepCol;
    
    while (currentRow !== toRow || currentCol !== toCol) {
      // Check bounds before accessing board
      if (currentRow < 0 || currentRow >= 9 || currentCol < 0 || currentCol >= 9) {
        return false; // Path goes out of bounds
      }
      
      if (board[currentRow][currentCol]) {
        return false;
      }
      currentRow += stepRow;
      currentCol += stepCol;
    }
    
    return true;
  }

  makeRamMove(board, fromRow, fromCol, toRow, toCol) {
    const newBoard = board.map(row => [...row]);
    const ram = newBoard[fromRow][fromCol];
    const targetPiece = newBoard[toRow][toCol];

    if (!targetPiece) {
      // Normal move if no piece to bump
      newBoard[fromRow][fromCol] = null;
      newBoard[toRow][toCol] = { ...ram, hasMoved: true };
      return { board: newBoard, captured: null, bumped: null };
    }

    // Calculate bump direction
    const deltaRow = toRow - fromRow;
    const deltaCol = toCol - fromCol;
    const bumpRow = toRow + (deltaRow > 0 ? 1 : deltaRow < 0 ? -1 : 0);
    const bumpCol = toCol + (deltaCol > 0 ? 1 : deltaCol < 0 ? -1 : 0);

    // Check if bump target is off the board (capture)
    if (bumpRow < 0 || bumpRow >= 9 || bumpCol < 0 || bumpCol >= 9) {
      // Capture the piece
      newBoard[fromRow][fromCol] = null;
      newBoard[toRow][toCol] = { ...ram, hasMoved: true };
      return { board: newBoard, captured: targetPiece, bumped: null };
    }

    // Perform the bump
    newBoard[fromRow][fromCol] = null;
    newBoard[toRow][toCol] = { ...ram, hasMoved: true };
    newBoard[bumpRow][bumpCol] = targetPiece;

    return { board: newBoard, captured: null, bumped: { piece: targetPiece, to: { row: bumpRow, col: bumpCol } } };
  }

  // Handle spy freeze when spy reaches home row
  handleSpyMove(board, piece, toRow, toCol) {
    if (piece.type === this.pieceTypes.SPY) {
      const homeRow = piece.color === 'white' ? 8 : 0;
      if (toRow === homeRow) {
        const enemyColor = piece.color === 'white' ? 'black' : 'white';
        
        // Find the enemy king's current position
        let kingRow = -1, kingCol = -1;
        for (let row = 0; row < 9; row++) {
          for (let col = 0; col < 9; col++) {
            const targetPiece = board[row][col];
            if (targetPiece && targetPiece.type === this.pieceTypes.KING && targetPiece.color === enemyColor) {
              kingRow = row;
              kingCol = col;
              break;
            }
          }
          if (kingRow !== -1) break;
        }
        
        if (kingRow !== -1) {
          this.spyFreezeMap.set(enemyColor, {
            spyPos: { row: toRow, col: toCol },
            kingFrozenAt: { row: kingRow, col: kingCol }
          });
        }
      }
    }
  }

  // Find the king of a specific color
  findKing(board, color) {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        const piece = board[row][col];
        if (piece && piece.type === this.pieceTypes.KING && piece.color === color) {
          return { row, col };
        }
      }
    }
    return null;
  }

  // Simplified donkey attack detection for check detection (avoids infinite loops)
  canDonkeyAttackSquare(board, fromRow, fromCol, toRow, toCol) {
    // Piece C: King-like movement (1 square in any direction)
    if (this.enablePieceValidation.DONKEY.C && this.isKingLikeMove(fromRow, fromCol, toRow, toCol)) {
      return true; // King moves don't need path checking
    }

    // Piece A: Vertical movement with alternating pattern (with path checking)
    if (this.enablePieceValidation.DONKEY.A && this.isPieceAMove(fromRow, fromCol, toRow, toCol)) {
      return this.isPieceAPathClear(board, fromRow, fromCol, toRow, toCol);
    }

    // Piece B: Horizontal movement with alternating pattern (with path checking)
    if (this.enablePieceValidation.DONKEY.B && this.isPieceBMove(fromRow, fromCol, toRow, toCol)) {
      return this.isPieceBPathClear(board, fromRow, fromCol, toRow, toCol);
    }

    return false;
  }

  // Check if a square is being attacked by the opponent
  isSquareAttacked(board, row, col, byColor) {
    for (let fromRow = 0; fromRow < 9; fromRow++) {
      for (let fromCol = 0; fromCol < 9; fromCol++) {
        const piece = board[fromRow][fromCol];
        if (piece && piece.color === byColor) {
          // Temporarily check if this piece can attack the target square
          // We need to bypass the king-in-check validation for this check
          if (this.canPieceAttackSquare(board, fromRow, fromCol, row, col)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Check if a piece can attack a specific square (used for check detection)
  canPieceAttackSquare(board, fromRow, fromCol, toRow, toCol) {
    const piece = board[fromRow][fromCol];
    if (!piece) return false;

    // Basic validation
    if (toRow < 0 || toRow >= 9 || toCol < 0 || toCol >= 9) {
      return false;
    }

    if (fromRow === toRow && fromCol === toCol) {
      return false;
    }

    // Piece-specific attack validation (similar to move validation but for attack purposes)
    switch (piece.type) {
      case this.pieceTypes.KING:
        const deltaRow = Math.abs(toRow - fromRow);
        const deltaCol = Math.abs(toCol - fromCol);
        return deltaRow <= 1 && deltaCol <= 1;
      
      case this.pieceTypes.KNIGHT:
        return this.validateKnightMove(fromRow, fromCol, toRow, toCol);
      
      case this.pieceTypes.PAWN:
        const direction = piece.color === 'white' ? 1 : -1;
        const deltaRowPawn = toRow - fromRow;
        const deltaColPawn = toCol - fromCol;
        // Pawns attack diagonally
        return Math.abs(deltaColPawn) === 1 && deltaRowPawn === direction;
      
      case this.pieceTypes.ADVISOR:
        return this.validateAdvisorMove(board, fromRow, fromCol, toRow, toCol);
      
      case this.pieceTypes.CANNON:
        // Cannons can ONLY attack pieces by jumping over exactly one piece
        const deltaRowCannon = toRow - fromRow;
        const deltaColCannon = toCol - fromCol;
        
        if (deltaRowCannon !== 0 && deltaColCannon !== 0) {
          return false; // Must attack horizontally or vertically
        }
        
        const targetPieceCannon = board[toRow][toCol];
        
        // Cannons cannot attack empty squares
        if (!targetPieceCannon) {
          return false;
        }
        
        // If target square has a piece, must jump over exactly one piece to capture
        return this.canCannonJumpAndCapture(board, fromRow, fromCol, toRow, toCol);
      
      case this.pieceTypes.CAMEL:
        return this.validateCamelMove(fromRow, fromCol, toRow, toCol);
      
      case this.pieceTypes.DONKEY:
        // Simplified donkey attack detection to avoid infinite loops
        // Don't use full validateDonkeyMove which might call back into check detection
        return this.canDonkeyAttackSquare(board, fromRow, fromCol, toRow, toCol);
      
      case this.pieceTypes.RAM: {
        // FIX (2026-08-01): rams don't attack empty squares. They attack by
        // BUMPING a piece — if the target is empty there's nothing to bump,
        // so it's not an attack. Prior implementation returned `true` for
        // any same-row/col empty square with clear path, treating rams like
        // rooks. That caused the "king can't move to empty d8 because
        // ram on h8 attacks it" bug: check-detection blocked a legal king
        // move because it thought the ram threatened the destination.
        //
        // Matches server-side C++ semantics (game.cc RAM case in
        // canPieceAttack): attack requires (a) same row/col with clear
        // path, (b) enemy piece on target, (c) bump destination behind
        // target is off-board OR occupied (for chain bumps). Special-case
        // for king targets: if bump destination is empty and on-board, the
        // king just gets pushed — that's not a capturing check.
        const deltaRowRam = toRow - fromRow;
        const deltaColRam = toCol - fromCol;
        if (deltaRowRam !== 0 && deltaColRam !== 0) return false;
        if (!this.isPathClear(board, fromRow, fromCol, toRow, toCol)) return false;

        const targetPieceRam = board[toRow][toCol];
        // Empty target: nothing to bump → not attacked.
        if (!targetPieceRam) return false;
        // Own piece: obviously not an attack (defense).
        if (targetPieceRam.color === piece.color) return false;

        // Compute the bump destination one step further in the ram's dir.
        const stepR = deltaRowRam === 0 ? 0 : (deltaRowRam > 0 ? 1 : -1);
        const stepC = deltaColRam === 0 ? 0 : (deltaColRam > 0 ? 1 : -1);
        const bumpR = toRow + stepR;
        const bumpC = toCol + stepC;
        const bumpOffBoard = bumpR < 0 || bumpR >= 9 || bumpC < 0 || bumpC >= 9;
        if (bumpOffBoard) return true;  // capture by pushing off the board
        // King target: only an attack if bump destination is occupied
        // (chain-bump captures the king). Empty bump destination just
        // pushes the king; not a capturing threat.
        if (targetPieceRam.type === this.pieceTypes.KING) {
          return !!board[bumpR][bumpC];
        }
        // Non-king target: attacks whether bump destination is empty or
        // occupied (chain-bump semantics for material capture).
        return true;
      }
      
      case this.pieceTypes.SPY:
        const deltaRowSpy = Math.abs(toRow - fromRow);
        const deltaColSpy = Math.abs(toCol - fromCol);
        return deltaRowSpy <= 1 && deltaColSpy <= 1;
      
      default:
        return false;
    }
  }

  // Check if a king is in check
  isKingInCheck(board, color) {
    const king = this.findKing(board, color);
    if (!king) return false;

    const opponentColor = color === 'white' ? 'black' : 'white';
    const isAttacked = this.isSquareAttacked(board, king.row, king.col, opponentColor);
    return isAttacked;
  }

  // Check if a move would leave the king in check (illegal move)
  wouldMoveLeaveKingInCheck(board, fromRow, fromCol, toRow, toCol) {
    const piece = board[fromRow][fromCol];
    if (!piece) return true;

    // Create a copy of the board to test the move
    const testBoard = board.map(row => [...row]);
    // eslint-disable-next-line no-unused-vars
    // Make the move on the test board
    testBoard[toRow][toCol] = testBoard[fromRow][fromCol];
    testBoard[fromRow][fromCol] = null;

    // Check if the king is in check after this move
    const result = this.isKingInCheck(testBoard, piece.color);
    
    return result;
  }

  // Enhanced move validation that includes check detection
  isValidMoveWithCheckDetection(board, fromRow, fromCol, toRow, toCol, currentPlayer) {
    // First check basic move validity
    const basicValid = this.isValidMove(board, fromRow, fromCol, toRow, toCol, currentPlayer);
    
    if (!basicValid) {
      return false;
    }

    // Check if this move would leave the king in check (illegal) - only if check validation is enabled
    if (this.enableCheckValidation) {
      const wouldLeaveInCheck = this.wouldMoveLeaveKingInCheck(board, fromRow, fromCol, toRow, toCol);
      
      if (wouldLeaveInCheck) {
        return false;
      }
    }

    return true;
  }

  generateAllLegalMoves(board, playerColor) {
    const legalMoves = [];
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        const piece = board[r][c];
        if (piece && piece.color === playerColor) {
          // Iterate over all possible destination squares
          for (let toRow = 0; toRow < 9; toRow++) {
            for (let toCol = 0; toCol < 9; toCol++) {
              if (this.isValidMoveWithCheckDetection(board, r, c, toRow, toCol, playerColor)) {
                legalMoves.push({
                  from: { row: r, col: c },
                  to: { row: toRow, col: toCol }
                });
              }
            }
          }
        }
      }
    }
    return legalMoves;
  }

  // Helper methods for debugging - toggle validation layers
  setValidationMode(mode) {
    console.log(`🔧 Setting validation mode to: ${mode}`);
    
    // Reset all piece validation first
    Object.keys(this.enablePieceValidation).forEach(key => {
      if (typeof this.enablePieceValidation[key] === 'object') {
        Object.keys(this.enablePieceValidation[key]).forEach(subKey => {
          this.enablePieceValidation[key][subKey] = false;
        });
      } else {
        this.enablePieceValidation[key] = false;
      }
    });
    
    switch (mode) {
      case 'bypass':
        this.bypassValidation = true;
        this.enableBasicValidation = true;
        this.enableCheckValidation = false;
        this.enableSpyValidation = false;
        break;
      case 'basic':
        this.bypassValidation = false;
        this.enableBasicValidation = true;
        this.enableCheckValidation = false;
        this.enableSpyValidation = false;
        break;
      case 'pawn-only':
        this.bypassValidation = false;
        this.enableBasicValidation = true;
        this.enableCheckValidation = false;
        this.enableSpyValidation = false;
        this.enablePieceValidation.PAWN = true;
        break;
      case 'simple-pieces':
        this.bypassValidation = false;
        this.enableBasicValidation = true;
        this.enableCheckValidation = false;
        this.enableSpyValidation = false;
        this.enablePieceValidation.PAWN = true;
        this.enablePieceValidation.KING = true;
        this.enablePieceValidation.KNIGHT = true;
        break;
      case 'most-pieces':
        this.bypassValidation = false;
        this.enableBasicValidation = true;
        this.enableCheckValidation = false;
        this.enableSpyValidation = false;
        this.enablePieceValidation.PAWN = true;
        this.enablePieceValidation.KING = true;
        this.enablePieceValidation.KNIGHT = true;
        this.enablePieceValidation.ADVISOR = true;
        this.enablePieceValidation.CANNON = true;
        this.enablePieceValidation.CAMEL = true;
        this.enablePieceValidation.RAM = true;
        this.enablePieceValidation.SPY = true;
        // Leave DONKEY disabled
        break;
      case 'full':
        this.bypassValidation = false;
        this.enableBasicValidation = true;
        this.enableCheckValidation = true;
        this.enableSpyValidation = true;
        Object.keys(this.enablePieceValidation).forEach(key => {
          if (typeof this.enablePieceValidation[key] === 'object') {
            Object.keys(this.enablePieceValidation[key]).forEach(subKey => {
              this.enablePieceValidation[key][subKey] = true;
            });
          } else {
            this.enablePieceValidation[key] = true;
          }
        });
        break;
      default:
        console.error('Unknown validation mode:', mode);
        break;
    }
    
    console.log(`🔧 Validation settings:`, {
      bypass: this.bypassValidation,
      basic: this.enableBasicValidation,
      pieces: this.enablePieceValidation,
      check: this.enableCheckValidation,
      spy: this.enableSpyValidation
    });
  }

  // Enable/disable specific piece validation
  setPieceValidation(pieceType, enabled) {
    const upperType = pieceType.toUpperCase();
    if (this.enablePieceValidation.hasOwnProperty(upperType)) {
      if (typeof this.enablePieceValidation[upperType] === 'object') {
        Object.keys(this.enablePieceValidation[upperType]).forEach(subKey => {
          this.enablePieceValidation[upperType][subKey] = enabled;
        });
      } else {
        this.enablePieceValidation[upperType] = enabled;
      }
      console.log(`🔧 ${enabled ? 'Enabled' : 'Disabled'} validation for ${pieceType}`);
    } else {
      console.error('Unknown piece type:', pieceType);
    }
  }

  // Get current validation mode
  getValidationMode() {
    if (this.bypassValidation) return 'bypass';
    
    const enabledPieces = Object.keys(this.enablePieceValidation).filter(
      key => this.enablePieceValidation[key]
    );
    
    if (enabledPieces.length === 0) return 'basic';
    if (enabledPieces.length === 1 && enabledPieces[0] === 'PAWN') return 'pawn-only';
    if (enabledPieces.length === 3 && enabledPieces.includes('PAWN') && 
        enabledPieces.includes('KING') && enabledPieces.includes('KNIGHT')) return 'simple-pieces';
    if (enabledPieces.length === 8) return 'most-pieces'; // All except DONKEY
    if (enabledPieces.length === 9 && this.enableCheckValidation && this.enableSpyValidation) return 'full';
    return `custom (${enabledPieces.join(', ')})`;
  }

  // Helper methods for enabling donkey A/B/C validation
  enableDonkeyValidation(component) {
    if (component === 'all') {
      this.enablePieceValidation.DONKEY.enabled = true;
      this.enablePieceValidation.DONKEY.A = true;
      this.enablePieceValidation.DONKEY.B = true;
      this.enablePieceValidation.DONKEY.C = true;
    } else if (['A', 'B', 'C'].includes(component)) {
      this.enablePieceValidation.DONKEY.enabled = true;
      this.enablePieceValidation.DONKEY[component] = true;
    }
    console.log(`🐴 Enabled donkey ${component} validation`);
  }

  disableDonkeyValidation(component) {
    if (component === 'all') {
      this.enablePieceValidation.DONKEY.enabled = false;
      this.enablePieceValidation.DONKEY.A = false;
      this.enablePieceValidation.DONKEY.B = false;
      this.enablePieceValidation.DONKEY.C = false;
    } else if (['A', 'B', 'C'].includes(component)) {
      this.enablePieceValidation.DONKEY[component] = false;
    }
    console.log(`🐴 Disabled donkey ${component} validation`);
  }

  getDonkeyValidationStatus() {
    return {
      enabled: this.enablePieceValidation.DONKEY.enabled,
      A: this.enablePieceValidation.DONKEY.A,
      B: this.enablePieceValidation.DONKEY.B,
      C: this.enablePieceValidation.DONKEY.C
    };
  }
}

export default GameLogic;

// Also export for Node.js (CommonJS)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GameLogic;
} 